import { useRef, useState } from 'react';
import { FileWithStatus, FileUploadStatus } from '@/types/upload';
import { useAssetUpload } from '@/hooks/useAssetUpload';
import { useQueryClient } from '@tanstack/react-query';
import { GET_ASSET_DEPENDENCIES_QUERY_KEY } from '@/hooks/api/assets/useGetAssetDependencies';

interface UseDependencyUploadOptions {
  onUploaded?: (dependencyPath: string, newAssetId: string) => void;
}

export const useDependencyUpload = (options?: UseDependencyUploadOptions) => {
  const pendingDependencyPath = useRef<string>('');
  const assetPathToDependencyPath = useRef<Map<string, string>>(new Map());
  const [uploadingPaths, setUploadingPaths] = useState<Set<string>>(new Set());
  const [pendingFile, setPendingFile] = useState<File | undefined>();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  const uploadContext = useAssetUpload({
    onSuccess: results => {
      for (const result of results) {
        if (result.status.state === FileUploadStatus.COMPLETED && result.assetId) {
          const depPath = assetPathToDependencyPath.current.get(result.originalPath ?? result.path);
          if (depPath) {
            options?.onUploaded?.(depPath, result.assetId);
          }
        }
      }
      assetPathToDependencyPath.current.clear();
      void queryClient.resetQueries({
        queryKey: [GET_ASSET_DEPENDENCIES_QUERY_KEY],
      });
      setUploadingPaths(new Set());
    },
  });

  const handleUploadClick = (dependencyPath: string) => {
    pendingDependencyPath.current = dependencyPath;
    const fileInput = uploadContext.fileInputReference.current;
    if (fileInput) {
      fileInput.click();
    }
  };

  const confirmUpload = () => {
    const dependencyPath = pendingDependencyPath.current;
    const file = pendingFile;
    if (!file || !dependencyPath) return;

    const assetPath = dependencyPath.includes('/')
      ? `${dependencyPath.slice(0, dependencyPath.lastIndexOf('/'))}/${file.name}`
      : file.name;

    const fileWithStatus: FileWithStatus = {
      id: dependencyPath,
      file,
      name: file.name,
      size: file.size,
      path: assetPath,
      status: {
        state: FileUploadStatus.PENDING,
      },
    };

    assetPathToDependencyPath.current.set(assetPath, dependencyPath);
    setUploadingPaths(previous => new Set(previous).add(dependencyPath));
    void uploadContext.handleFilesAdded([fileWithStatus], []);

    setPendingFile(undefined);
    setConfirmOpen(false);
    pendingDependencyPath.current = '';
  };

  const cancelUpload = () => {
    setPendingFile(undefined);
    setConfirmOpen(false);
    pendingDependencyPath.current = '';
  };

  const createFileInputProperties = (): React.InputHTMLAttributes<HTMLInputElement> => ({
    ...uploadContext.fileInputProps,
    multiple: false,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const dependencyPath = pendingDependencyPath.current;

      if (dependencyPath && event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        setPendingFile(file);
        setConfirmOpen(true);
        event.target.value = '';
      } else {
        uploadContext.fileInputProps.onChange?.(event);
      }
    },
  });

  return {
    ...uploadContext,
    uploadingPaths,
    handleUploadClick,
    fileInputProps: createFileInputProperties(),
    pendingFile,
    pendingDependencyPath: pendingDependencyPath.current,
    confirmOpen,
    confirmUpload,
    cancelUpload,
  };
};
